/**
 * Serialises the search state (query, filters, page) to and from URL search
 * params, so the result view survives navigation, reloads and login redirects
 * and can be shared as a link.
 *
 * Only filter fields the backend understands are read from the URL, so
 * unrelated params (utm_*, fbclid, …) are never sent as filters.
 */

import type { SearchFilters } from '../types/search';

export interface SearchUrlState {
  query: string;
  filters: SearchFilters;
  page: number;
  /**
   * Selected keyword IDs. Not a search filter: they narrow the result set on
   * the client (docs/concepts/concept_curated_keywords.md §10.4).
   */
  keywords: string[];
}

/** Multi-value facet filters; each value is a repeated param (`?source=a&source=b`). */
export const URL_ARRAY_FILTERS = ['source', 'source_type', 'language', 'zenon_version'] as const;

/** Single-value date-range filters. */
export const URL_DATE_FILTERS = [
  'created_after',
  'created_before',
  'updated_after',
  'updated_before',
] as const;

const QUERY_PARAM = 'q';
const PAGE_PARAM = 'page';
const KEYWORDS_PARAM = 'keyword_ids';

export function parseSearchUrl(params: URLSearchParams): SearchUrlState {
  const filters: SearchFilters = {};
  for (const key of URL_ARRAY_FILTERS) {
    const values = params.getAll(key).filter(Boolean);
    if (values.length > 0) filters[key] = values;
  }
  for (const key of URL_DATE_FILTERS) {
    const value = params.get(key);
    if (value) filters[key] = value;
  }

  const rawPage = Number(params.get(PAGE_PARAM));
  const page = Number.isInteger(rawPage) && rawPage > 1 ? rawPage : 1;
  const keywords = [...new Set(params.getAll(KEYWORDS_PARAM).filter(Boolean))];

  return { query: params.get(QUERY_PARAM) ?? '', filters, page, keywords };
}

/** Canonical params for a search state: `q`, filters in a fixed order, keywords, then `page` (> 1 only). */
export function buildSearchParams(state: SearchUrlState): URLSearchParams {
  const params = new URLSearchParams();
  params.set(QUERY_PARAM, state.query);
  for (const key of URL_ARRAY_FILTERS) {
    const values = state.filters[key];
    if (Array.isArray(values)) {
      for (const value of values) params.append(key, value);
    }
  }
  for (const key of URL_DATE_FILTERS) {
    const value = state.filters[key];
    if (typeof value === 'string' && value) params.set(key, value);
  }
  for (const keyword of state.keywords ?? []) params.append(KEYWORDS_PARAM, keyword);
  if (state.page > 1) params.set(PAGE_PARAM, String(state.page));
  return params;
}
